import React from 'react';
import { CustomTooltip } from 'app/components/Common/Tooltips';
import { useRef, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { missingField } from 'app/constants';

const useStyles = makeStyles()(theme => ({
  rowSectionEllipsis: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '250px',
    width: '100% !important',
    fontSize: '14px',
    fontWeight: 'normal',
    color: theme.palette.framework.system.charcoal,
  },
  rowSubsectionContainer: {
    width: '100%',
    whiteSpace: 'nowrap',
    paddingBottom: '12px',
    WebkitFlexWrap: 'nowrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  rowSubsectionHeader: {
    fontWeight: '600',
    paddingRight: '24px',
    whiteSpace: 'nowrap',
  },
  rowSubsectionData: {
    fontSize: '14px',
    fontWeight: '400',
    width: '100%',
    paddingBottom: '12px',
  },
  rowContainer: {
    width: '100%',
    whiteSpace: 'nowrap',
    paddingBottom: '6px',
    WebkitFlexWrap: 'nowrap',
    alignItems: 'flex-end',
  },
  rowHeader: {
    fontWeight: '600',
    paddingRight: '24px',
    whiteSpace: 'nowrap',
    minWidth: '200px',
  },
  tooltipText: {
    maxWidth: '300px',
  },
  url: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

export const RowSubSection: React.FC<{
  header: string | number | React.ReactNode;
  data: string | number | React.ReactNode;
}> = props => {
  const { classes } = useStyles();

  return (
    <Grid container direction="column">
      <Grid container direction="row" className={classes.rowSubsectionContainer}>
        <Grid
          item
          className={classes.rowSubsectionHeader}
          sx={{
            fontSize: props.data ? '12px' : '14px',
          }}
        >
          <Typography component="span" variant="body1">
            {props.header}
          </Typography>
        </Grid>
      </Grid>
      <Grid item className={classes.rowSubsectionData}>
        <Typography component="span" variant="body2">
          {props.data}
        </Typography>
      </Grid>
    </Grid>
  );
};

export const RowSection: React.FC<{
  header: string | number | React.ReactNode;
  data: string | number | React.ReactNode;
  key?: number;
  isUrl?: boolean;
}> = props => {
  const [showTooltip, setShowTooltip] = useState(false);
  const rowSectionRef = useRef(null);
  const { classes } = useStyles();

  const handleMouseEnter = () => {
    setShowTooltip(rowSectionRef?.current?.scrollWidth > rowSectionRef?.current?.clientWidth);
  };

  const prependHttpsIfMissing = url => {
    if (!url?.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  return (
    <Box sx={{ minWidth: '100%', maxWidth: '0px' }}>
      <Grid container direction="row" key={props.key} className={classes.rowContainer}>
        <Grid
          item
          className={classes.rowHeader}
          style={{
            fontSize: '12px',
          }}
        >
          {props.header}
        </Grid>
        <Grid
          item
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          <CustomTooltip
            open={showTooltip}
            placement="bottom"
            arrow={true}
            classes={{ tooltip: classes.tooltipText }}
            tooltipContent={props.data}
          >
            <div
              className={classes.rowSectionEllipsis}
              ref={rowSectionRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={() => setShowTooltip(false)}
            >
              {props.isUrl && props?.data !== missingField ? (
                <a href={String(prependHttpsIfMissing(props?.data))} target="_blank" rel="noopener noreferrer" className={classes.url}>
                  {props?.data ?? ''}
                </a>
              ) : (
                props?.data
              )}
            </div>
          </CustomTooltip>
        </Grid>
        {props.children}
      </Grid>
    </Box>
  );
};

/**
 *  Component that wraps text with ellipsis when character exceeds
 */
export const TruncatedText = ({ text, maxLength }: { text: string; maxLength: number }) => {
  return (
    <Box
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-line',
      }}
    >
      <Typography sx={{ fontSize: '14px' }}>
        {text.length > maxLength ? `${text.slice(0, maxLength)}...` : text}
      </Typography>
    </Box>
  );
};
